/**
 * Levelling control centre — status / method / track metadata shared by
 * rm-portal and rm-portal-beta (PD-1180 / PROD-45).
 *
 * The canonical evaluation lifecycle is the six-state model from the control
 * centre design. Today the engine (document_lf_evaluation.status) persists only
 * DRAFT / COMPLETED / ARCHIVED, so `deriveEvaluationStatus` can produce only
 * `not-started`, `in-progress` and `levelled`. `invited`, `review` and
 * `sent-back` are defined here so pills, filters and row actions light up as
 * soon as the backend records those states — until then they simply never occur.
 *
 * Icons: `icon` is a Material Symbols name (rm-portal-beta), `mdi` the MDI
 * equivalent (rm-portal / Vuetify 2).
 */

/**
 * Compact label for a long factor name, for chips and column headers where the
 * full name will not fit: the first two words, minus any short connective
 * ("and", "&", "of"). "Problem Solving & Critical Thinking" reads as "Problem
 * Solving"; "Autonomy & Decision Authority" as "Autonomy". Always pair it with
 * the full name in a tooltip - the short form is not unambiguous on its own.
 */
export const shortName = (name) => {
    const parts = name.split(' ');
    const shortened = [];
    for (const part of parts.splice(0, 2)) {
        if (part.length > 3) {
            shortened.push(part);
        }
    }

    return shortened.join(' ');
}

export const LEVELLING_STATUS = {
  'not-started': { label: 'Not evaluated', short: 'Not evaluated', tone: 'todo', icon: 'radio_button_unchecked', mdi: 'mdi-radiobox-blank', stage: 0 },
  'invited': { label: 'Invited', short: 'Invited', tone: 'teal', icon: 'person', mdi: 'mdi-account-arrow-right-outline', stage: 1 },
  'in-progress': { label: 'In progress', short: 'In progress', tone: 'accent', icon: 'pending', mdi: 'mdi-progress-clock', stage: 2 },
  'review': { label: 'Awaiting approval', short: 'Needs review', tone: 'warn', icon: 'rate_review', mdi: 'mdi-comment-search-outline', stage: 3 },
  'sent-back': { label: 'Sent back', short: 'Sent back', tone: 'violet', icon: 'undo', mdi: 'mdi-undo-variant', stage: 3 },
  'levelled': { label: 'Approved', short: 'Approved', tone: 'green', icon: 'check_circle', mdi: 'mdi-check-circle', stage: 4 },
};

export const LEVELLING_STATUS_ORDER = ['not-started', 'invited', 'in-progress', 'review', 'sent-back', 'levelled'];

/**
 * Map a `Report/evaluationStatus` row to a lifecycle key. Rows carry the latest
 * ACTIVE evaluation's status; no row (or ARCHIVED) means the official process
 * hasn't started, regardless of whether a job-level field happens to be filled.
 */
export function deriveEvaluationStatus(evalRow) {
  const s = evalRow && evalRow.evaluation_status;
  if (s === 'COMPLETED') return 'levelled';
  if (s === 'DRAFT') return 'in-progress';
  return 'not-started';
}

export const LEVELLING_METHOD = {
  evaluate: { label: 'Evaluated', short: 'Eval', icon: 'fact_check', mdi: 'mdi-clipboard-check-outline' },
  classify: { label: 'Classified', short: 'Class', icon: 'auto_awesome_motion', mdi: 'mdi-shape-outline' },
};

/* Anchor roles (PD-1258). One source for the anchor mark in both portals, so the
   control-centre chip, the calibration reference tag and the compare badge all
   read the same labels and icons. An anchor is an EVALUATED role flagged as a
   calibration reference; the CEO ceiling is a separate flag layered on top of it,
   fixing the top of the register (one per client). */
export const LEVELLING_ANCHOR = {
  anchor: {
    label: 'Anchor role',
    short: 'Anchor',
    icon: 'anchor',
    mdi: 'mdi-anchor',
    tip: 'Anchor role — an evaluated reference other roles calibrate against.',
  },
  ceiling: {
    label: 'CEO ceiling',
    short: 'CEO',
    icon: 'vertical_align_top',
    mdi: 'mdi-format-vertical-align-top',
    tip: 'CEO ceiling — this anchor fixes the top of your framework.',
  },
  /* Picked (by the setup wizard) but not yet evaluated: a claim, not evidence. It
     gets its OWN glyph rather than the anchor one — dashed-and-grey styling alone
     was too easy to miss, so a pending anchor read as a confirmed one. */
  pending: {
    label: 'Anchor role (pending)',
    short: 'Pending',
    icon: 'pending',
    mdi: 'mdi-clock-outline',
    tip: 'Anchor role (pending) — selected as an anchor; needs a full evaluation to confirm.',
  },
};

/**
 * Options for the Anchor filter facet, in the order they should read: most
 * governed first. Shared so both portals label and value them identically — the
 * value strings are the `anchorIncluded` contract with getLevellingList.
 */
export const LEVELLING_ANCHOR_FILTER_OPTIONS = [
  { value: 'ceiling', label: LEVELLING_ANCHOR.ceiling.label },
  { value: 'anchor', label: LEVELLING_ANCHOR.anchor.label },
  { value: 'none', label: 'Not an anchor' },
];

/* Why a Compare row is present even though it fails the user's level filter: the
   server force-includes the family's anchor (PD-1258). A row that ignores the
   filter must explain itself, or it reads as the filter being broken. */
export const LEVELLING_ANCHOR_FORCED_NOTE =
  'Always shown as this family’s calibration reference, even when your level filter would exclude it.';

/**
 * The in-journey anchor control (PD-1258), per the prototype's LvlAnchorSummaryPill.
 *
 * ONE quiet pill, not an inline checkbox with a textarea: everything explanatory —
 * what an anchor is, the required rationale, how to back out — lives in the modal, so
 * the summary itself stays about the evaluation. The flag is still applied on SAVE;
 * the pill only edits draft state.
 *
 * Three states, and the third is the point: `needs` is ticked-but-unexplained, which
 * a checkbox cannot express. It is reachable when a draft was saved with the intent
 * but no rationale, and it has to read as unfinished rather than done.
 *
 * @param {{on?: boolean, note?: string, family?: string}} draft
 */
export function anchorDraftPill(draft) {
  const d = draft || {};
  const on = !!d.on;
  const note = String(d.note || '').trim();
  const needs = on && !note;
  return {
    on,
    needs,
    label: on ? 'Anchor role' : 'Mark as anchor',
    /* The trailing glyph carries the state: nothing to fix / amend / add. */
    icon: needs ? 'error' : on ? 'edit' : 'add',
    mdi: needs ? 'mdi-alert-circle-outline' : on ? 'mdi-pencil' : 'mdi-plus',
    tip: needs
      ? 'Anchor role — a rationale is required before this can be saved as an anchor. Click to add it.'
      : on
        ? `Saving as an anchor role for ${d.family || 'its family'}. Click to review the rationale or undo.`
        : 'Not an anchor. Click to learn what anchor roles are and mark this one.',
  };
}

/**
 * The anchor note shown at the START of an evaluation (PD-1258), per the prototype's
 * `anchorNote` in the welcome step.
 *
 * Rationale quality matters more on a calibration reference than anywhere else, so the
 * journey says so before the first question rather than at the end. Three variants,
 * because "you are about to create one", "one exists but is unevidenced" and "one is
 * already in force" are different situations to walk into.
 *
 * @param {{intent?: boolean, isAnchor?: boolean, pending?: boolean, family?: string}} o
 * @returns {{lead: string, body: string} | null} null when there is nothing to say
 */
export function anchorIntroNote(o) {
  const x = o || {};
  if (!x.intent && !x.isAnchor) return null;
  const lead = x.intent && !x.isAnchor
    ? 'This evaluation will set an anchor role.'
    : x.isAnchor && x.pending
      ? 'This role is a pending anchor.'
      : 'This is an anchor role.';
  const who = x.family ? `other ${x.family} roles` : 'other roles in its family';
  return {
    lead,
    body: `It becomes the calibration reference ${who} are compared against — take extra`
      + ' care with your answers and rationales, as they set “what good looks like”.',
  };
}

/**
 * The anchor lock on the who-does-it screen ("Set the level for this role").
 *
 * This is the screen a user actually LANDS on after choosing "Evaluate as anchor
 * role", so the constraint is stated here — the disabled method cards one screen
 * later are only the backstop. Without it the user picks who scores the role with no
 * idea that classification is off the table, and no way back out of the anchor.
 *
 * `escape` is that way out, and it matters: the alternative is abandoning the journey
 * to go and unset the anchor elsewhere. Only offered when the caller can honour it.
 *
 * Also carries the two choice-card descriptions, which the prototype swaps under the
 * lock — "the full evaluation" rather than "the factors", and a note on the invite card
 * that a colleague cannot classify their way out of it either.
 *
 * @param {Object} o { locked, family, canEscape }
 */
export function anchorLockNote(o) {
  const x = o || {};
  if (!x.locked) {
    return {
      locked: false,
      nowDesc: 'Work through the factors yourself and confirm the level.',
      inviteDesc: 'Send it to a colleague to score, then review their submission.',
    };
  }
  return {
    locked: true,
    lead: 'This role is being evaluated as an anchor.',
    body: `It will become the calibration reference for ${x.family || 'its family'}, so it needs`
      + ' a full, point-scored evaluation — classification isn’t available on this path,'
      + ' whoever does the scoring.',
    escapeLead: ' If you only need a quick placement, ',
    escapeLabel: 'level this role without anchoring',
    canEscape: !!x.canEscape,
    nowDesc: 'Work through the full evaluation yourself and confirm the level.',
    inviteDesc: 'Send it to a colleague to score. They’ll be given the full evaluation —'
      + ' anchors can’t be classified.',
  };
}

/**
 * Copy for the "Suggested"/"From title" badges in the anchor wizard.
 *
 * The tooltip differs by SIGNAL, and that difference is the whole point of the badge:
 * a suggestion drawn from evaluated seniority is evidence, while one drawn from a job
 * title is a guess that the user is being asked to check. Saying "Suggested" without
 * distinguishing them would present the guess as the finding.
 *
 * @param {Object} o { fromTitle: boolean, kind: 'ceo' | 'family' }
 */
export function anchorSuggestionBadge(o) {
  const x = o || {};
  if (!x.fromTitle) {
    return {
      label: 'Suggested',
      tip: x.kind === 'ceo'
        ? 'Suggested because it is the most senior evaluated role on the register.'
        : 'Suggested from evaluated seniority and headcount — multiple incumbents make a'
          + ' more defensible reference.',
      weak: false,
    };
  }
  return {
    label: x.kind === 'ceo' ? 'Suggested' : 'From title',
    tip: x.kind === 'ceo'
      ? 'Suggested from its job title — nothing is levelled yet, so there is no evaluated'
        + ' seniority to read. Check it before continuing.'
      : 'Nothing in this family is levelled yet, so seniority is read from job title and'
        + ' headcount. Indicative — check it before confirming.',
    weak: true,
  };
}

/** Audit event → human label for the anchor history timeline. */
export const LEVELLING_ANCHOR_EVENTS = {
  ANCHOR_SET: { label: 'Marked as an anchor role', icon: 'anchor', mdi: 'mdi-anchor', tone: 'accent' },
  ANCHOR_NOTE: { label: 'Anchor rationale updated', icon: 'edit_note', mdi: 'mdi-note-edit-outline', tone: null },
  ANCHOR_UNSET: { label: 'Anchor status removed', icon: 'anchor', mdi: 'mdi-anchor', tone: null },
  CEILING_SET: { label: 'Set as the CEO ceiling', icon: 'vertical_align_top', mdi: 'mdi-format-vertical-align-top', tone: 'accent' },
  CEILING_UNSET: { label: 'CEO ceiling removed', icon: 'vertical_align_top', mdi: 'mdi-format-vertical-align-top', tone: 'warn' },
};

/** Label for one anchor audit row, expanding the "ceiling moved to X" case. */
export function anchorEventLabel(event) {
  const meta = LEVELLING_ANCHOR_EVENTS[event && event.event] || null;
  if (!meta) return null;
  if (event.event === 'CEILING_UNSET' && event.moved_to_title) {
    return { ...meta, label: `CEO ceiling moved to ${event.moved_to_title}` };
  }
  return meta;
}

/* Events that record the rationale AS IT STOOD at that moment. The others pass no
   rationale at all, so their NULL means "not applicable" rather than "was empty" —
   they must be skipped when looking back, not read as a cleared value. */
const ANCHOR_RATIONALE_EVENTS = new Set(['ANCHOR_SET', 'ANCHOR_NOTE']);

/**
 * The rationale in force immediately BEFORE `rows[i]`, or null if there wasn't one.
 *
 * `ANCHOR_NOTE` on its own only says a rationale changed, not what it changed from,
 * which is exactly the question an auditor asks. No column stores the old value —
 * and none needs to: every rationale-bearing event already records the text as of
 * that event, so the previous value is the one on the next-older such event. Derived
 * rather than stored, so it also works for history captured before this existed.
 *
 * @param {Array} rows  the history as the endpoint returns it — NEWEST FIRST
 * @param {number} i    index of the row being rendered
 */
export function anchorPreviousRationale(rows, i) {
  if (!Array.isArray(rows)) return null;
  for (let j = i + 1; j < rows.length; j += 1) {
    const row = rows[j];
    if (row && ANCHOR_RATIONALE_EVENTS.has(row.event)) return row.rationale || null;
  }
  return null;
}

/** document_lf_evaluation.flow_type → method key ('classify' | 'evaluate'). */
export function methodFromFlowType(flowType) {
  if (!flowType) return null;
  return flowType === 'CLASSIFICATION' ? 'classify' : 'evaluate';
}

/* Career tracks for the level chip. Grade codes are client-configured strings;
   the chip only decorates codes whose alphabetic prefix matches a known track. */
export const LEVELLING_TRACKS = [
  { key: 'O', letter: 'O', label: 'Operational', tint: '#5C7A1E', tintBg: '#F4F7E8' },
  { key: 'IC', letter: 'I', label: 'Individual contributor', tint: '#0D6E96', tintBg: '#E8F4FB' },
  { key: 'M', letter: 'M', label: 'Management', tint: '#8A6A00', tintBg: '#FBF4E0' },
  { key: 'E', letter: 'E', label: 'Executive', tint: '#9B2A5B', tintBg: '#FBEAF1' },
];

/** 'IC3' → the IC track meta; unknown prefixes → null (chip renders plain). */
export function trackFromLevelCode(code) {
  const m = String(code || '').trim().match(/^([A-Za-z]+)/);
  if (!m) return null;
  const prefix = m[1].toUpperCase();
  return LEVELLING_TRACKS.find((t) => t.key === prefix) || null;
}

/** Track meta by explicit track code/abbreviation (e.g. 'IC' from the endpoint),
 *  for levels whose code is a bare number ("10") with no track prefix to parse. */
export function trackByCode(trackCode) {
  const key = String(trackCode || '').trim().toUpperCase();
  if (!key) return null;
  return LEVELLING_TRACKS.find((t) => t.key === key) || null;
}

/** Track meta by track LABEL (e.g. 'Operational', 'Individual Contributor').
 *  Lets a client whose abbreviation differs from the canonical key (e.g. 'S' for
 *  Operational) still resolve to the design's track tint. Case-insensitive. */
export function trackByLabel(trackLabel) {
  const l = String(trackLabel || '').trim().toLowerCase();
  if (!l) return null;
  return LEVELLING_TRACKS.find((t) => t.label.toLowerCase() === l) || null;
}

/* Document status is a separate, client-configured axis (doc_status_text) —
   labels are arbitrary, so the quiet pill's tone comes from a heuristic. */
export function docStatusTone(label) {
  const s = String(label || '').toLowerCase();
  if (!s) return 'muted';
  if (/approv|publish|complete|live/.test(s)) return 'ok';
  if (/review|await|pending/.test(s)) return 'warn';
  return 'muted';
}

/* ---- "Updated" column helpers ---- */

const DAY_MS = 86400000;

function toMs(value) {
  if (!value) return null;
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  const parsed = Date.parse(value);
  return isNaN(parsed) ? null : parsed;
}

/** Relative "updated" stamp: Today / Yesterday / N days ago / weeks / months. */
export function relativeTime(value) {
  const ms = toMs(value);
  if (ms == null) return '—';
  const d = Math.floor((Date.now() - ms) / DAY_MS);
  if (d <= 0) return 'Today';
  if (d === 1) return 'Yesterday';
  if (d < 7) return d + ' days ago';
  if (d < 14) return 'Last week';
  if (d < 30) return Math.floor(d / 7) + ' weeks ago';
  if (d < 60) return 'Last month';
  return Math.floor(d / 30) + ' months ago';
}

/** True when the timestamp falls inside the recency window (default 7 days). */
export function isRecent(value, days = 7) {
  const ms = toMs(value);
  return ms != null && Date.now() - ms < days * DAY_MS;
}
